import {createFileRoute} from '@tanstack/react-router';
import React, {useEffect, useRef, useState} from 'react';

import {apiAction, apiActionImproved} from '../api';
import {ClubType} from '../constants';
import {useJwtAuth} from '../JWTAuthProvider';
import {setAdsUserData} from '../utilities/adsEmailMatching.ts';
import {startGoogleSignIn} from '../utilities/googleAuth.ts';
import {useSports} from '@/sports/registry.ts';
import {storedSportKey} from '@/utilities/sportKeys';
import {QUIZ_FUNNEL_CSS, quizSports, recordQuizResponse} from './quiz-community';

export const Route = createFileRoute('/scorepal-quiz')({
    component: ScorePalQuizPage,
    head: () => ({meta: [{title: 'Tell us how you run your events | ScorePal'}, {name: 'robots', content: 'noindex'}]}),
});

/**
 * /scorepal-quiz/ — six questions about how an organiser runs and pays for events today (sport,
 * how they collect money, what would save them time, how they'd want to pay, how a $19.99 pass
 * for 16 players lands, what would stop them), then the way in: build an event now in the /quiz
 * builder, or explore with a free account.
 *
 * Its answers are market research as much as a funnel, so every one is recorded as it is given
 * (variant=pricing, its own tab on Master → Quiz Responses) — someone who stops at question four
 * still counts for questions one to three.
 */
const VARIANT = 'pricing';
// Its own response key, not the one /quiz and /quiz-community share: the server files a row under
// the funnel of its FIRST write, so anyone who had been through either of those in this browser
// had their answers here written onto that row and filed under its funnel, never this one.
const SQ_KEY = 'sp_sq_rkey';
const record = (payload: Record<string, any> = {}) => recordQuizResponse(VARIANT, payload, SQ_KEY);

type Option = {val: string; label: string};

const COLLECT: Option[] = [
    {val: 'cash_transfer', label: 'Cash or bank transfer on the day'},
    {val: 'payment_links', label: 'Payment links (PayPal, Stripe link, etc.)'},
    {val: 'booking_app', label: 'Through a booking app or venue system'},
    {val: 'free', label: 'Players don’t pay, it’s free to play'},
];
const TIME_SAVERS: Option[] = [
    {val: 'self_registration', label: 'Players registering and paying themselves'},
    {val: 'whatsapp', label: 'Running sign-ups through WhatsApp'},
    {val: 'automation', label: 'Automatic draws, fixtures and standings'},
    {val: 'live_scores', label: 'Live scores and results players can follow'},
    {val: 'tracking', label: 'Tracking players and who owes money'},
];
const PAY_MODEL: Option[] = [
    {val: 'per_event', label: 'Per event, only when I run one'},
    {val: 'yearly', label: 'One yearly payment'},
    {val: 'monthly', label: 'Monthly subscription'},
    {val: 'registration_fee', label: 'A small fee taken from player registrations, nothing upfront'},
    {val: 'free_only', label: 'I’d only use a free tool'},
];
const PASS_16: Option[] = [
    {val: 'cheap', label: 'Cheap, I’d pay without thinking'},
    {val: 'fair', label: 'Fair, I’d pay'},
    {val: 'expensive_but_worth', label: 'Expensive, but I’d pay if it saved me time'},
    {val: 'too_expensive', label: 'Too expensive, I wouldn’t pay'},
];
const BLOCKER: Option[] = [
    {val: 'infrequent', label: 'I don’t run events often enough'},
    {val: 'players_wont_switch', label: 'My players won’t use a new app'},
    {val: 'free_tools', label: 'Free tools / WhatsApp work well enough'},
    {val: 'see_it_first', label: 'I’d need to see it work first'},
    {val: 'price', label: 'The price'},
];
const MAX_TIME_SAVERS = 2;

// The spinning-logo welcome the other quizzes open with, once per browser session. Decided in the
// first render, not an effect, so nobody who skips it sees a frame of it first.
const SPLASH_MS = 7500;
const SPLASH_KEY = 'sp_sq_splash_seen';
let splashDecided: boolean | null = null;
const openingPhase = (): 'splash' | 'quiz' => {
    if (typeof window === 'undefined') return 'splash';
    if (splashDecided === null) {
        try { splashDecided = !sessionStorage.getItem(SPLASH_KEY); } catch { splashDecided = false; }
    }
    return splashDecided ? 'splash' : 'quiz';
};
const QUESTIONS = 6;

function ScorePalQuizPage() {
    const jwt = useJwtAuth();
    useSports(); // the full sport list once the live registry lands

    const [phase, setPhase] = useState<'splash' | 'quiz'>(openingPhase);
    const [step, setStep] = useState(1);
    const [sport, setSport] = useState('');
    const [collect, setCollect] = useState('');
    const [timeSavers, setTimeSavers] = useState<string[]>([]);
    const [payModel, setPayModel] = useState('');
    const [pass16, setPass16] = useState('');
    const [blocker, setBlocker] = useState('');
    const [nextStep, setNextStep] = useState<'' | 'create' | 'explore'>('');
    const [choosingEvent, setChoosingEvent] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const top = useRef<HTMLDivElement | null>(null);

    useEffect(() => { top.current?.scrollIntoView({block: 'start'}); }, [step]);

    useEffect(() => {
        try { sessionStorage.setItem(SPLASH_KEY, '1'); } catch { /* private mode: it just plays again */ }
        if (!splashDecided) {
            setPhase('quiz');
            return;
        }
        const t = setTimeout(() => {
            splashDecided = false; // played — later visits this session skip it
            setPhase('quiz');
        }, SPLASH_MS);
        return () => clearTimeout(t);
    }, []);

    // Every answer so far, rewritten whole on each record (the endpoint keeps the latest object).
    const answers = (extra: Record<string, string> = {}) => ({
        ...(sport ? {sport} : {}),
        ...(collect ? {collect} : {}),
        ...(timeSavers.length ? {time_savers: timeSavers.join(',')} : {}),
        ...(payModel ? {pay_model: payModel} : {}),
        ...(pass16 ? {pass_16: pass16} : {}),
        ...(blocker ? {blocker} : {}),
        ...(nextStep ? {next_step: nextStep} : {}),
        ...extra,
    });

    // A single-choice answer: record it and move on.
    const choose = (key: string, setter: (v: string) => void, next: number) => (val: string) => {
        setter(val);
        record({answers: answers({[key]: val})});
        setStep(next);
    };

    const toggleTimeSaver = (val: string) => setTimeSavers((prev) => {
        if (prev.includes(val)) return prev.filter((v) => v !== val);
        if (prev.length >= MAX_TIME_SAVERS) return [...prev.slice(1), val]; // a third pick replaces the oldest
        return [...prev, val];
    });

    // Create now: the /quiz builder opens straight on "Set up your tournament / league" for their
    // sport (the same session answers its own questions write, and #setup skips its splash and
    // questions), then its signup, as on /quiz.
    const createNow = (kind: 'tournament' | 'league') => {
        setNextStep('create');
        record({answers: answers({next_step: 'create', event_kind: kind}), completed: true});
        const known = ['padel', 'pickleball', 'tennis', 'badminton', 'squash', 'football', 'basketball', 'volleyball', 'cricket', 'esports', 'roundnet'];
        // The option's value is the stored key, and pickleball's is spelt 'pickelball'; the builder's is 'pickleball'.
        const builderSport = sport === 'pickelball' ? 'pickleball' : sport;
        const quizAnswers = known.includes(builderSport)
            ? {'1': kind, '2': builderSport}
            : {'1': kind, '2': 'other', '2_other': sport === 'table_tennis' ? 'Table tennis' : ''};
        try { sessionStorage.setItem('sp_quiz_answers', JSON.stringify(quizAnswers)); } catch { /* the builder asks instead */ }
        window.location.assign('/quiz/#setup');
    };

    const explore = () => {
        setNextStep('explore');
        record({answers: answers({next_step: 'explore'})});
        setStep(QUESTIONS + 2);
    };

    const googleSignup = () => {
        record({answers: answers(), completed: true});
        startGoogleSignIn('/profiles/me/');
    };

    const signup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || !password) {
            setError('Please fill in every field.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                quiz_key: localStorage.getItem(SQ_KEY) || undefined, // links users_analytics to this quiz row
                platform: 'web',
                profile: {email: email.trim(), password, name: name.trim(), lastname: '', inviter_uid: '', leaderboard_enrolled: true},
                questionnaire: {
                    sport: sport ? storedSportKey(sport) : null,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    club: {
                        type: ClubType.Community, name: `${name.trim()}'s Events`,
                        country_code: '', city: '', city_coordinates: null, meta: {},
                        organizer_path: 'community',
                    },
                },
            });
            if (res?.error) { setError(res.error); setBusy(false); return; }
            jwt.setFieldsFromResponse(res);
            record({answers: answers(), completed: true}); // the token is stored — links the row to the account
            sessionStorage.setItem('welcome_tutorial_seen', '"y"');
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            // The same conversion events /quiz and /quiz-community send.
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                setAdsUserData(email.trim());
                gtag('event', 'sign_up', {method: 'scorepal', signup_type: 'organizer'});
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_', transaction_id: res?.user?.uid || ''});
                if (res?.user?.uid) sessionStorage.setItem(`sp_signup_fired:${res.user.uid}`, '1');
            }
            window.location.assign('/profiles/me/');
        } catch (err: any) {
            setError(err?.message || 'Something went wrong. Please try again.');
            setBusy(false);
        }
    };

    const pct = Math.min(100, Math.round((Math.min(step, QUESTIONS) / QUESTIONS) * 100));
    const stepLabel = step <= QUESTIONS ? `Question ${step} of ${QUESTIONS}` : step === QUESTIONS + 1 ? 'Your next step' : 'Create your account';
    const back = (to: number) => <button type="button" className="back" onClick={() => setStep(to)}>← Back</button>;
    const options = (list: Option[], picked: string, onPick: (v: string) => void) => list.map((o) => (
        <button type="button" key={o.val} className={`opt${picked === o.val ? ' sel' : ''}`} onClick={() => onPick(o.val)}>{o.label}</button>
    ));

    return (
        <div className="sp-quiz" ref={top}>
            <style>{QUIZ_FUNNEL_CSS}</style>
            <div className="wrap">
                {phase === 'splash' && (
                    <div className="sp-splash">
                        <div className="pill"><img src="/favicon-180x180.png" alt="ScorePal" width={118} height={118} /></div>
                        <p className="word">Welcome to ScorePal</p>
                        <p className="tag">The new way to manage your sporting events</p>
                        <div className="ltrack"><div className="lbar" /></div>
                        <p className="lmsg">Let's get started.</p>
                    </div>
                )}

                {phase === 'quiz' && <>
                <div className="qhead">
                    <div className="pmeta"><span>{stepLabel}</span><span>{pct}%</span></div>
                    <div className="ptrack"><div className="pbar" style={{width: `${pct}%`}} /></div>
                </div>

                <div className="qstep" key={step}>
                    {step === 1 && <>
                        <h1>What sport do you organise?</h1>
                        <div className="opts2">
                            {quizSports().map((s) => (
                                <button type="button" key={s.val} className={`opt${sport === s.val ? ' sel' : ''}`}
                                        onClick={() => choose('sport', setSport, 2)(s.val)}>
                                    <span className="oemoji">{s.emoji}</span>{s.label}
                                </button>
                            ))}
                        </div>
                    </>}

                    {step === 2 && <>
                        <h1>How do you collect money from players today?</h1>
                        {options(COLLECT, collect, choose('collect', setCollect, 3))}
                        {back(1)}
                    </>}

                    {step === 3 && <>
                        <h1>Which of these would save you the most time?</h1>
                        <p className="sub">Pick up to {MAX_TIME_SAVERS}.</p>
                        {TIME_SAVERS.map((o) => (
                            <button type="button" key={o.val} aria-pressed={timeSavers.includes(o.val)}
                                    className={`opt${timeSavers.includes(o.val) ? ' sel' : ''}`} onClick={() => toggleTimeSaver(o.val)}>
                                {o.label}
                            </button>
                        ))}
                        <button type="button" className="opt osubmit" disabled={!timeSavers.length}
                                onClick={() => { record({answers: answers()}); setStep(4); }}>
                            Continue →
                        </button>
                        {back(2)}
                    </>}

                    {step === 4 && <>
                        <h1>How would you prefer to pay for a tool like ScorePal?</h1>
                        {options(PAY_MODEL, payModel, choose('pay_model', setPayModel, 5))}
                        {back(3)}
                    </>}

                    {step === 5 && <>
                        <h1>For an event of 16 players, a $19.99 Event Pass feels:</h1>
                        {options(PASS_16, pass16, choose('pass_16', setPass16, 6))}
                        {back(4)}
                    </>}

                    {step === 6 && <>
                        <h1>What’s the main thing that would stop you paying?</h1>
                        {options(BLOCKER, blocker, choose('blocker', setBlocker, 7))}
                        {back(5)}
                    </>}

                    {step === 7 && <>
                        <h1>Shall we create your event now or do you want to explore the platform?</h1>
                        <p className="sub">Creating your event takes about two minutes, and your account is made as part of it.</p>
                        {!choosingEvent ? (
                            <button type="button" className="opt osubmit" onClick={() => setChoosingEvent(true)}>Create my event now →</button>
                        ) : (
                            <>
                                <p className="sub" style={{marginBottom: 10}}>What are you setting up?</p>
                                <div className="opts2">
                                    <button type="button" className="opt" onClick={() => createNow('tournament')}>🏆 A tournament</button>
                                    <button type="button" className="opt" onClick={() => createNow('league')}>📅 A league</button>
                                </div>
                            </>
                        )}
                        <button type="button" className="opt oalt" onClick={explore}>Explore the platform first →</button>
                        {back(6)}
                    </>}

                    {step === 8 && <>
                        <h1>Create your account</h1>
                        <p className="sub">Free to start. You can set up your first event from inside whenever you’re ready.</p>
                        <button type="button" className="opt ogoogle" onClick={googleSignup}>
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" /><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z" /></svg>
                            Continue with Google
                        </button>
                        <div className="or-div">or</div>
                        <form onSubmit={signup}>
                            <label htmlFor="sq-name">Your name</label>
                            <input id="sq-name" className="tin" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                            <label htmlFor="sq-email">Email</label>
                            <input id="sq-email" className="tin" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                            <label htmlFor="sq-password">Password</label>
                            <input id="sq-password" className="tin" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                            {error && <div className="err">{error}</div>}
                            <button type="submit" className="opt osubmit" disabled={busy}>{busy ? 'Creating your account…' : 'Create my account →'}</button>
                        </form>
                        {back(7)}
                        <p className="small">
                            By joining you agree to our <a href="/terms/" target="_blank" rel="noreferrer" style={{color: '#0B3DB0', textDecoration: 'underline'}}>Terms</a>.
                            Already with us? <a href="/login/" style={{color: '#0B3DB0', textDecoration: 'underline'}}>Log in</a>.
                        </p>
                    </>}
                </div>
                </>}
            </div>
        </div>
    );
}
